import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Progress, message as antMessage } from 'antd';
import { ArrowLeftOutlined, InboxOutlined, DeleteOutlined, FileOutlined, LoadingOutlined } from '@ant-design/icons';
import './Dashboard.css';

// 可搜索下拉组件
function SearchableSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const filtered = options.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedName = options.find(o => o.id === value)?.name || '';

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => { setOpen(!open); setSearch(''); }}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          fontSize: 14,
          cursor: 'pointer',
          background: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: selectedName ? '#333' : '#bfbfbf'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedName || placeholder}
        </span>
        <span style={{ fontSize: 10, color: '#999', marginLeft: 8 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          background: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: 6,
          marginTop: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 300,
          overflow: 'auto'
        }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              autoFocus
              placeholder="输入搜索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                fontSize: 13,
                outline: 'none'
              }}
            />
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: 13 }}>无匹配项</div>
          ) : (
            filtered.map(o => (
              <div
                key={o.id}
                onClick={() => { onChange(o.id); setOpen(false); setSearch(''); }}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: 14,
                  background: o.id === value ? '#e6f7ff' : 'transparent',
                  borderBottom: '1px solid #f5f5f5'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.background = o.id === value ? '#e6f7ff' : 'transparent'}
              >
                {o.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || '';

export default function WorkLogEntry() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [focus, setFocus] = useState('');
  const [detail, setDetail] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, percent, fileName }
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/workorder/projects`);
      const data = await resp.json();
      if (data.success) {
        const projects = data.projects.map(name => ({ id: name, name }));
        // 添加“其他”选项
        projects.push({ id: '其他', name: '其他' });
        setGroups(projects);
      }
    } catch (e) {
      showMessage('加载项目列表失败', 'error');
    }
    setLoading(false);
  };

  const handleGroupChange = async (groupId) => {
    setSelectedGroup(groupId);
    if (!groupId) return;
    
    try {
      const resp = await fetch(`${API_URL}/api/work-logs/yesterday-focus?projectId=${groupId}`, {
        headers: { 'x-token': localStorage.getItem('notes_token') }
      });
      const data = await resp.json();
      if (data.focus) {
        setFocus(data.focus);
      }
    } catch (e) {
      console.error('加载昨日重点失败', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGroup || !date || !focus || !detail) {
      showMessage('请填写完整信息', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // Step 1: 创建日志
      const resp = await fetch(`${API_URL}/api/work-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-token': localStorage.getItem('notes_token')
        },
        body: JSON.stringify({
          project_id: selectedGroup,
          project_name: groups.find(g => g.id === selectedGroup)?.name || '',
          date,
          focus,
          detail,
          remarks
        })
      });

      if (!resp.ok) {
        const err = await resp.json();
        showMessage(err.error || '提交失败', 'error');
        setSubmitting(false);
        return;
      }

      const result = await resp.json();
      const logId = result.id;

      // Step 2: 上传附件（如果有）
      if (pendingFiles.length > 0) {
        const total = pendingFiles.length;
        for (let i = 0; i < pendingFiles.length; i++) {
          const file = pendingFiles[i];
          setUploadProgress({
            current: i + 1,
            total,
            percent: 0,
            fileName: file.name
          });

          const formData = new FormData();
          formData.append('files', file);

          await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const percent = Math.round((e.loaded / e.total) * 100);
                setUploadProgress({
                  current: i + 1,
                  total,
                  percent,
                  fileName: file.name
                });
              }
            });
            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                setUploadProgress({
                  current: i + 1,
                  total,
                  percent: 100,
                  fileName: file.name
                });
                resolve();
              } else {
                reject(new Error(`上传失败: ${file.name}`));
              }
            });
            xhr.addEventListener('error', () => reject(new Error(`网络错误: ${file.name}`)));
            xhr.open('POST', `${API_URL}/api/work-logs/${logId}/attachments`);
            xhr.setRequestHeader('x-token', localStorage.getItem('notes_token'));
            xhr.send(formData);
          });
        }
      }

      setUploadProgress(null);
      setPendingFiles([]);
      showMessage('提交成功！', 'success');
      setFocus('');
      setDetail('');
      setRemarks('');
      // 不重置项目和日期，方便继续录入
    } catch (e) {
      console.error('提交失败:', e);
      showMessage('提交失败', 'error');
    }
    setSubmitting(false);
  };

  const showMessage = (msg, type) => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage(''), 3000);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFilesSelect = (files) => {
    const fileArray = Array.from(files);
    if (pendingFiles.length + fileArray.length > 10) {
      antMessage.warning('最多选择10个附件');
      return;
    }
    // 暂存文件，不上传
    setPendingFiles(prev => [...prev, ...fileArray]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesSelect(e.dataTransfer.files);
    }
  }, [pendingFiles.length]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesSelect(e.target.files);
      e.target.value = ''; // reset so same file can be selected again
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-logs')} style={{ marginRight: 16 }}>
          返回
        </Button>
        <h2 style={{ margin: 0 }}>📅 工作日志录入</h2>
      </div>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: 16,
          borderRadius: 6,
          background: message.type === 'success' ? '#f6ffed' : '#fff2f0',
          border: `1px solid ${message.type === 'success' ? '#b7eb8f' : '#ffccc7'}`,
          color: message.type === 'success' ? '#52c41a' : '#ff4d4f'
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              项目 <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <SearchableSelect
              options={groups}
              value={selectedGroup}
              onChange={handleGroupChange}
              placeholder="请选择项目"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              日期 <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 14
            }}
            required
          />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            当前工作重点 <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <input
            type="text"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            placeholder="请输入当前工作重点"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 14
            }}
            required
          />
          <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
            自动带出昨日该项目的重点，可修改
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontWeight: 500 }}>
              详细工作内容 <span style={{ color: '#ff4d4f' }}>*</span>
            </label>
            <button
              type="button"
              onClick={() => {
                const lines = detail.split('\n').filter(l => l.trim());
                const numbered = lines.map((line, i) => `${i + 1}. ${line.replace(/^\d+\.\s*/, '')}`).join('\n');
                setDetail(numbered);
              }}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                background: '#f0f0f0',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              title="自动添加编号"
            >
              自动编号
            </button>
          </div>
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="请输入详细工作内容，可多行输入"
            rows={8}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.5,
              resize: 'vertical'
            }}
            required
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontWeight: 500 }}>
              其他说明（问题/备注/重要事项）
            </label>
            <button
              type="button"
              onClick={() => {
                const lines = remarks.split('\n').filter(l => l.trim());
                const numbered = lines.map((line, i) => `${i + 1}. ${line.replace(/^\d+\.\s*/, '')}`).join('\n');
                setRemarks(numbered);
              }}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                background: '#f0f0f0',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              title="自动添加编号"
            >
              自动编号
            </button>
          </div>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="请输入其他说明、问题、备注或重要事项，可多行输入"
            rows={4}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.5,
              resize: 'vertical'
            }}
          />
        </div>

        {/* 附件上传区域 - 在提交按钮上方 */}
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 500 }}>
            📎 附件 ({pendingFiles.length}/10)
          </h3>

          {/* 拖拽/点击上传区域 */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleClickUpload}
            style={{
              border: `2px dashed ${dragOver ? '#1890ff' : '#d9d9d9'}`,
              borderRadius: 8,
              padding: '24px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragOver ? '#e6f7ff' : '#fafafa',
              transition: 'all 0.3s',
              marginBottom: 16
            }}
          >
            <InboxOutlined style={{ fontSize: 32, color: dragOver ? '#1890ff' : '#999' }} />
            <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>
              拖拽文件到此处，或<span style={{ color: '#1890ff' }}>点击选择</span>
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
              支持多文件，最多10个，单文件不超过50MB
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* 待上传文件列表 */}
          {pendingFiles.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {pendingFiles.map((file, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  marginBottom: 8
                }}>
                  <FileOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {file.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      {formatFileSize(file.size)} · 待上传
                    </div>
                  </div>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemovePendingFile(idx)}
                    title="移除"
                  />
                </div>
              ))}
            </div>
          )}

          {pendingFiles.length === 0 && (
            <div style={{ textAlign: 'center', color: '#999', fontSize: 13, padding: '12px 0' }}>
              暂未选择附件
            </div>
          )}
        </div>

        {/* 上传进度 - 提交时显示 */}
        {uploadProgress && (
          <div style={{
            marginBottom: 16,
            padding: '12px 16px',
            background: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: 6
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <LoadingOutlined style={{ color: '#1890ff', fontSize: 16 }} />
              <span style={{ fontSize: 14, color: '#333' }}>
                正在上传附件 ({uploadProgress.current}/{uploadProgress.total})
              </span>
            </div>
            <div style={{
              fontSize: 12,
              color: '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              marginBottom: 4
            }}>
              {uploadProgress.fileName}
            </div>
            <Progress
              percent={uploadProgress.percent}
              size="small"
              status="active"
              style={{ margin: 0 }}
            />
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || loading}
          style={{
            width: '100%',
            padding: '10px 24px',
            background: '#1890ff',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 16,
            cursor: submitting || loading ? 'not-allowed' : 'pointer',
            opacity: submitting || loading ? 0.6 : 1
          }}
        >
          {submitting ? (uploadProgress ? '上传附件中...' : '提交中...') : '提交日志'}
        </button>
      </form>
    </div>
  );
}
